/**
 * In this file we build the application specific translator.
 *
 */
import { createTranslator, createTranslatorLanguage } from '@prospective/lithium';

import { markIntentionalReload } from '@/func/intentionalReload';

import de_CH from './strings/de_CH.js';
import de_DE from './strings/de_DE.js';
import en_US from './strings/en_US.js';
import fr_CH from './strings/fr_CH.js';
import gsw_CH from './strings/gsw_CH.js';
import it_CH from './strings/it_CH.js';

export interface LanguageList {
  [key: string]: {
    label: string;
    country: string;
  };
}

export const availableLanguages: LanguageList = {
  de_CH: {
    label: 'Deutsch',
    country: 'Schweiz',
  },
  de_DE: {
    label: 'Deutsch',
    country: 'Deutschland',
  },
  en_US: {
    label: 'English',
    country: 'United States',
  },
  gsw_CH: {
    label: 'Schwiizertüütsch',
    country: 'Schwiiz',
  },
};

const navLang = navigator.language.replace('-', '_');
const navLangLanguage = navLang.split('_')[0];
let defaultLanguage = 'de_CH';

// try direct match
if (Object.keys(availableLanguages).indexOf(navLang) > -1) {
  defaultLanguage = navLang;
}

// try machting language only
for (const lang in availableLanguages) {
  if (lang.split('_')[0] === navLangLanguage) {
    defaultLanguage = lang;
    break;
  }
}

export const currentLanguage = window.localStorage.getItem('language') || defaultLanguage;

export function setLanguage(language: string) {
  window.localStorage.setItem('language', language);
  // the reload applies the new locale; it isn't the user leaving, so unsaved-work guards shouldn't prompt
  markIntentionalReload();
  window.location.reload();
}

const translatorLanguages = [
  createTranslatorLanguage('de_CH', ['de_DE'], de_CH),
  createTranslatorLanguage('gsw_CH', ['de_CH'], gsw_CH),
  createTranslatorLanguage('de_DE', ['en_US'], de_DE),
  createTranslatorLanguage('fr_CH', ['en_US'], fr_CH),
  createTranslatorLanguage('it_CH', ['en_US'], it_CH),
  createTranslatorLanguage('en_US', [], en_US),
];

const translator = createTranslator(currentLanguage, translatorLanguages);

export default translator;

const POSTING_FALLBACK_LANGUAGE = 'en_US';
// languages that only have strings for the candidate-facing pages, so they stay out of the language selector
const postingOnlyLanguages = ['fr_CH', 'it_CH'];
const postingLanguages = [...Object.keys(availableLanguages), ...postingOnlyLanguages];

/** maps a job posting language (`de`, `de-CH`, `en_US`, ...) to one of the available languages */
export function resolvePostingLanguage(language: string | null | undefined): string {
  if (!language) return currentLanguage;

  const normalized = language.trim().replace('-', '_');
  if (postingLanguages.includes(normalized)) return normalized;

  const languageCode = normalized.split('_')[0].toLowerCase();
  // keep the visitor's own regional variant, e.g. de_CH for a Swiss visitor of a `de` posting
  if (currentLanguage.split('_')[0] === languageCode) return currentLanguage;

  return postingLanguages.find((lang) => lang.split('_')[0] === languageCode) ?? POSTING_FALLBACK_LANGUAGE;
}

/**
 * Translator for pages a candidate opens from a message (upload and chat links), which follow the job
 * posting's language rather than the visitor's settings. Postings in a language without strings get English.
 */
export function createPostingTranslator(language: string | null | undefined) {
  return createTranslator(resolvePostingLanguage(language), translatorLanguages);
}
